/**
 * Aufträge – wo steht jeder laufende Vorgang, und was fehlt als Nächstes?
 *
 * Die Arbeits-Sicht für das ganze Team, ohne Schloss: Hier stehen keine
 * Beträge, nur Schritte. Die Geld-Sicht ist die Auftragsauswertung.
 *
 * Eine Liste, nicht vier Blöcke (v14, Kapitel 17). Bis v13 standen die Phasen
 * als eigene Abschnitte untereinander; wer nach Gewerk oder nach Person
 * sortieren wollte, bekam vier getrennte Sortierungen und musste viermal
 * hinsehen. Jetzt gibt es eine Tabelle, und wie sie geordnet ist, entscheidet
 * „Gruppieren ▾" – Phase ist nur die Vorgabe.
 *
 * Die Leiste oben zeigt die Verteilung über die neun Schritte. Ihre Farben
 * sind die Rubrikfarben von „Mein Tag": Dieselbe Arbeit heißt überall gleich
 * und sieht überall gleich aus.
 *
 * Eine Zeile je Bestellung, nicht je Auftrag: Fenster und Beschattung aus zwei
 * Werken stehen in verschiedenen Phasen.
 *
 * Schritte werden im Vorgang quittiert, nicht hier. Ein Klick auf die Nummer
 * öffnet ihn. Kein Weiterklicken aus der Liste – jeder Schritt braucht Datum,
 * Person und Beleg und schreibt in die Chronik.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { Nachrichtenknopf } from '../komponenten/ChatKontext';
import { KoraMaskenhinweise } from '../komponenten/KoraHinweise';
import { useKora, useMeta } from '../zustand';
import { Menueknopf } from '../komponenten/Shell';
import { LiegtBei } from '../komponenten/LiegtBei';
import { useRubrikton } from '../hilfen/rubrikton';
import {
  Gruppenkopf,
  Gruppieren,
  Listensuche,
  Ordnerzelle,
  Spaltenkopf,  gewerkAus,
  naechsteSortierung,
  vergleich,
  type Sortierung,} from '../komponenten/Liste';
import { StationenBahnen, type Bahn } from '../komponenten/StationenBahnen';
import type { BahnId } from '../hilfen/bahnen';
import type { AbwicklungAntwort, AbwicklungZeile, ArchivEintrag, RechnungenAntwort } from '../typen';
import './abwicklung.css';

/** Die Spalten, nach denen sortiert werden kann (v14, 17.5). */
const SPALTEN: Record<
  string,
  { label: string; wert: (z: AbwicklungZeile) => string | number | null; leer?: (z: AbwicklungZeile) => boolean; klasse?: string; rechts?: boolean }
> = {
  vorgang: { label: 'Vorgang', wert: (z) => z.angebotName.toLowerCase() },
  fortschritt: { label: 'Fortschritt', wert: (z) => z.schrittIndex, klasse: 'aw-c-fort' },
  stand: { label: 'Stand', wert: (z) => z.tageImSchritt, klasse: 'aw-c-stand' },
  fehlt: {
    label: 'Was fehlt',
    /* Hänger zuerst, dann Warnungen, dann „im Plan" – nicht alphabetisch. */
    wert: (z) => (z.haenger?.stufe === 'rot' ? 0 : z.haenger ? 1 : 2),
    klasse: 'aw-c-fehlt',
  },
  termin: {
    label: 'Liefertermin',
    wert: (z) => z.lieferSortierung,
    leer: (z) => !z.lieferSortierung,
    klasse: 'aw-c-termin',
    rechts: true,
  },
  wer: {
    label: 'Liegt bei',
    wert: (z) => z.liegtBei?.personName ?? null,
    leer: (z) => !z.liegtBei?.person,
  },
};

/**
 * Die elf Stationen der Übersicht (STATIONEN-LISTEN.md, 4).
 *
 * Sie sind nicht dasselbe wie die neun Knoten einer Bestellung: Die Kette
 * beginnt seit v12 beim Aufmaß und endet bei „Montiert" – abgenommen wird
 * der Auftrag, nicht die einzelne Bestellung. Die Übersicht zeigt trotzdem
 * beide Ränder, weil sie den ganzen Weg erzählt: „Auftrag erteilt" als das,
 * was vor dem ersten Knoten liegt, und „Abgenommen · Service" als das, was
 * danach kommt.
 *
 * Station 1 bleibt deshalb in der Produktion leer – jede Bestellung startet
 * im Aufmaß. Station 11 zählt die Aufträge, die ein offener Servicepunkt
 * oder ein offener Mangel zurückgeholt hat (v14, 9.7).
 */
const STATIONEN: { key: string; name: string; phase: string }[] = [
  { key: 'erteilt', name: 'Auftrag erteilt', phase: 'beschaffung' },
  { key: 'aufmass', name: 'Aufmaß / Klärung', phase: 'beschaffung' },
  { key: 'bestellt', name: 'Bestellung raus', phase: 'beschaffung' },
  { key: 'ab_da', name: 'AB erhalten', phase: 'beschaffung' },
  { key: 'retour', name: 'AB unterfertigt retour', phase: 'beschaffung' },
  { key: 'produktion', name: 'In Produktion', phase: 'lieferung' },
  { key: 'termin', name: 'Termin bestätigt', phase: 'lieferung' },
  { key: 'geliefert', name: 'Geliefert', phase: 'lieferung' },
  { key: 'montage', name: 'Montage terminiert', phase: 'lieferung' },
  { key: 'montiert', name: 'Montiert', phase: 'lieferung' },
  { key: 'fertig', name: 'Abgenommen · Service', phase: 'service' },
];

/**
 * Steht die Zeile in dieser Station?
 *
 * Der Sonderfall ist die letzte: Ein Auftrag im Service trägt als Schritt
 * weiter „montiert" – er darf dort nicht ein zweites Mal mitzählen.
 */
function inStation(z: AbwicklungZeile, key: string): boolean {
  if (key === 'fertig') return z.phase === 'service';
  return z.phase !== 'service' && z.schritt === key;
}

/**
 * Wer bei welchem Schritt dran ist (STATIONEN-BAHNEN.md 1).
 *
 * Feste Tabelle, nicht je Auftrag entschieden. Beim Lieferanten liegt, was
 * bestellt ist und was in Produktion läuft; beim Kunden die AB, solange sie
 * zur Unterschrift draußen ist. Alles andere liegt bei uns.
 */
const PARTEI_VON: Record<string, BahnId> = {
  erteilt: 'eh',
  aufmass: 'eh',
  bestellt: 'lief',
  ab_da: 'eh',
  retour: 'kunde',
  produktion: 'lief',
  termin: 'eh',
  geliefert: 'eh',
  montage: 'eh',
  montiert: 'eh',
  fertig: 'eh',
};

const BAHNEN_AUFTRAEGE: { id: BahnId; name: string }[] = [
  { id: 'eh', name: 'Eichenherz' },
  { id: 'lief', name: 'Lieferant' },
  { id: 'kunde', name: 'Kunde' },
];

const GRUPPEN = [
  { key: 'phase', label: 'Phase' },
  { key: 'gewerk', label: 'Gewerk' },
  { key: 'wer', label: 'Liegt bei' },
  { key: 'keine', label: 'Keine Gruppierung' },
];

export function Auftragsabwicklung() {
  const meta = useMeta();
  const navigate = useNavigate();
  const { toastZeigen } = useKora();
  const ton = useRubrikton();
  const { phasen, schritte } = meta.abwicklung;

  const [schrittFilter, setSchrittFilter] = useState<string | null>(null);
  /* Bahnfilter: die Partei, auf die gefiltert ist (STATIONEN-BAHNEN.md 3). */
  const [parteiFilter, setParteiFilter] = useState<BahnId | null>(null);
  const [suche, setSuche] = useState('');
  const [sort, setSort] = useState<Sortierung>({ key: 'stand', richtung: 'ab' });
  const [gruppierung, setGruppierung] = useState('phase');
  const [zu, setZu] = useState<Record<string, boolean>>({});
  const [archivOffen, setArchivOffen] = useState(false);
  const [archivAlle, setArchivAlle] = useState(false);
  const [archivSuche, setArchivSuche] = useState('');

  const abfrage = useQuery({
    queryKey: ['abwicklung'],
    queryFn: () => api.hole<AbwicklungAntwort>('/abwicklung'),
  });
  const daten = abfrage.data;
  const zeilen = useMemo(() => daten?.zeilen ?? [], [daten]);

  /** Der Ton einer Phase – die Rubrikfarbe, die auch „Mein Tag" zeigt (17.3). */
  const phaseTon = (key: string) => ton(phasen.find((p) => p.key === key)?.rubrik ?? key);
  /* Die Zeile trägt die Farbe ihrer Phase, nicht die ihres Schrittes: Ein
     Auftrag im Service steht im Service, auch wenn sein letzter Schritt
     „montiert" heißt. */
  const zeilenTon = (z: AbwicklungZeile) => phaseTon(z.phase);

  const gefiltert = useMemo(() => {
    const q = suche.trim().toLowerCase();
    return zeilen
      .filter(
        (z) =>
          (!schrittFilter || inStation(z, schrittFilter)) &&
          (!parteiFilter || PARTEI_VON[z.schritt] === parteiFilter) &&
          (!q ||
            [z.angebotName, z.kunde, z.ort, z.lieferant ?? '', z.prefix]
              .join(' ')
              .toLowerCase()
              .includes(q)),
      )
      .slice()
      .sort((a, b) => vergleich(a, b, sort, SPALTEN));
  }, [zeilen, schrittFilter, parteiFilter, suche, sort]);

  /*
   * Gruppen: Phase in fester Reihenfolge, alles andere nach Anzahl
   * absteigend (17.6). Eine alphabetische Gewerksliste begänne mit dem
   * Gewerk, von dem es zwei Vorgänge gibt.
   */
  const gruppen = useMemo(() => {
    const schluesselVon = (z: AbwicklungZeile) =>
      gruppierung === 'phase'
        ? z.phase
        : gruppierung === 'gewerk'
          ? gewerkAus(z.angebotName) || '—'
          : gruppierung === 'wer'
            ? z.liegtBei?.person || '—'
            : 'alle';

    const eimer = new Map<string, AbwicklungZeile[]>();
    for (const z of gefiltert) {
      const k = schluesselVon(z);
      if (!eimer.has(k)) eimer.set(k, []);
      eimer.get(k)!.push(z);
    }
    let keys = [...eimer.keys()];
    if (gruppierung === 'phase') {
      const ordnung: string[] = phasen.map((p) => p.key);
      keys.sort((a, b) => ordnung.indexOf(a) - ordnung.indexOf(b));
    } else if (gruppierung !== 'keine') {
      keys.sort((a, b) => eimer.get(b)!.length - eimer.get(a)!.length);
    }

    return keys.map((k) => {
      const drin = eimer.get(k)!;
      const haengt = drin.filter((z) => z.haenger?.stufe === 'rot').length;
      const beachten = drin.filter((z) => z.haenger?.stufe === 'terrakotta').length;
      const titel =
        gruppierung === 'phase'
          ? (phasen.find((p) => p.key === k)?.label ?? k)
          : gruppierung === 'gewerk'
            ? `${k} · ${meta.kategorien.find((x) => x.prefix === k)?.label ?? 'Sonstige'}`
            : gruppierung === 'wer'
              ? k === '—'
                ? 'Niemand zuständig'
                : (drin[0]?.liegtBei?.personName ?? k)
              : '';
      return {
        key: k,
        titel,
        punkt: gruppierung === 'phase' ? phaseTon(k).akz : undefined,
        meldung: haengt
          ? `${haengt} ${haengt === 1 ? 'hängt' : 'hängen'}${beachten ? ` · ${beachten} beachten` : ''}`
          : beachten
            ? `${beachten} beachten`
            : 'im Plan',
        ton: haengt ? ('krit' as const) : beachten ? ('warn' as const) : undefined,
        anzahl: drin.length,
        zeilen: drin,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gefiltert, gruppierung, phasen, meta.kategorien]);

  /* Je Station ihre Anzahl und die Ungeöffneten darin (STATIONEN-BAHNEN.md 2.5). */
  const anzahlIn = (key: string) => zeilen.filter((z) => inStation(z, key)).length;
  const neuIn = (key: string) => zeilen.filter((z) => inStation(z, key) && z.neu).length;
  /*
   * Der Bahnkopf zeigt bei Aufträgen die Anzahl, nicht einen Betrag: An der
   * Bestellung hängt keine Summe – die steht am Angebot, und ein Auftrag
   * besteht aus mehreren Bestellungen. Eine erfundene Zahl wäre schlimmer
   * als keine.
   */
  const anzahlBahn = (keys: string[]) =>
    zeilen.filter((z) => keys.some((k) => inStation(z, k))).length;

  /*
   * Die drei Bahnen (STATIONEN-BAHNEN.md 1).
   *
   * Jede Spalte ist ein Schritt und steht in jeder Bahn; die Kachel liegt
   * nur bei der Partei, die dran ist. So sieht man senkrecht, was beim
   * Lieferanten hängt und was bei uns.
   */
  const bahnen: Bahn[] = BAHNEN_AUFTRAEGE.map((b) => {
    const eigeneKeys = STATIONEN.filter((st) => PARTEI_VON[st.key] === b.id).map((st) => st.key);
    const wieviele = anzahlBahn(eigeneKeys);
    return {
      id: b.id,
      name: b.name,
      summe: `${wieviele} ${wieviele === 1 ? 'Vorgang' : 'Vorgänge'}`,
      summeSchwach: true,
      schritte: STATIONEN.map((st) => ({
        key: st.key,
        label: st.name,
        anzahl: anzahlIn(st.key),
        neu: neuIn(st.key),
        eigen: PARTEI_VON[st.key] === b.id,
      })),
    };
  });

  function oeffnen(z: AbwicklungZeile | ArchivEintrag) {
    navigate(`/angebot/${z.angebotId}`, { state: { herkunft: 'abwicklung' } });
  }

  const archivTreffer = useMemo(() => {
    const liste = daten?.archiv ?? [];
    const q = archivSuche.trim().toLowerCase();
    const g = q
      ? liste.filter(
          (e) =>
            e.angebotName.toLowerCase().includes(q) ||
            e.kunde.toLowerCase().includes(q) ||
            e.prefix.toLowerCase() === q ||
            e.abnahmeAm.startsWith(q),
        )
      : liste;
    return archivAlle || q ? g : g.slice(0, 5);
  }, [daten, archivSuche, archivAlle]);

  return (
    <div className="aw">
      <div className="aw-kopf">
        <div className="eh-kopf-links">
          <Menueknopf />
          <div className="aw-titelzeile">
            <h1 className="aw-titel">Aufträge</h1>
            {daten && (
              <span className="aw-untertitel">
                {daten.laufend} laufende {daten.laufend === 1 ? 'Vorgang' : 'Vorgänge'} · Stand{' '}
                {daten.standText}
              </span>
            )}
          </div>
        </div>
        <div className="eh-kopf-rechts">
          <Nachrichtenknopf />
        </div>
      </div>

      {abfrage.isLoading && <div className="eh-karte">Die Abwicklung wird geladen …</div>}
      {abfrage.isError && (
        <div className="eh-karte">Die Abwicklung konnte nicht geladen werden.</div>
      )}

      {daten && (
        <>
          <StationenBahnen
            titel="Wo die Aufträge stehen"
            bahnen={bahnen}
            schritt={schrittFilter}
            partei={parteiFilter}
            hinweis={
              schrittFilter
                ? `gefiltert auf „${STATIONEN.find((st) => st.key === schrittFilter)?.name}" · nochmal klicken hebt auf`
                : parteiFilter
                  ? `gefiltert auf „${BAHNEN_AUFTRAEGE.find((b) => b.id === parteiFilter)?.name}" · nochmal klicken hebt auf`
                  : 'Klick auf Schritt oder Bahn filtert · Farbe je Bahn über den Punkt'
            }
            onSchritt={(k) => {
              setSchrittFilter((alt) => (alt === k ? null : k));
              setParteiFilter(null);
            }}
            onPartei={(id) => {
              setParteiFilter((alt) => (alt === id ? null : id));
              setSchrittFilter(null);
            }}
          />

          <div className="li-werkzeuge">
            <Listensuche wert={suche} onAendern={setSuche} />
            <div className="li-werkzeuge-rechts">
              <span className="li-sorthinweis">
                {sort.key
                  ? `sortiert nach ${SPALTEN[sort.key]!.label} ${sort.richtung === 'auf' ? '▲' : '▼'}`
                  : 'ohne Sortierung'}
              </span>
              <Gruppieren
                optionen={GRUPPEN}
                wert={gruppierung}
                onWaehlen={(k) => {
                  setGruppierung(k);
                  setZu({});
                }}
              />
            </div>
          </div>

          <div className="li-tabelle">
            <div className="aw-zeile li-kopfzeile">
              {Object.entries(SPALTEN).map(([key, s]) => (
                <Spaltenkopf
                  key={key}
                  spalte={key}
                  label={s.label}
                  klasse={s.klasse}
                  rechts={s.rechts}
                  sort={sort}
                  onSort={(sp) => setSort((alt) => naechsteSortierung(alt, sp))}
                />
              ))}
              {/* Die Ordnerspalte hat keinen Kopf – ihr Symbol erklärt sich. */}
              <span />
            </div>

            {gruppen.map((g) => (
              <div key={g.key} className="aw-gruppe">
                {gruppierung !== 'keine' && (
                  <Gruppenkopf
                    label={g.titel}
                    punkt={g.punkt}
                    zusatz={g.meldung}
                    zusatzTon={g.ton}
                    rechts={`${g.anzahl} ${g.anzahl === 1 ? 'Vorgang' : 'Vorgänge'}`}
                    offen={!zu[g.key]}
                    onKlappen={() => setZu((x) => ({ ...x, [g.key]: !x[g.key] }))}
                  />
                )}
                {!zu[g.key] &&
                  g.zeilen.map((z) => (
                    <Zeile
                      key={z.id}
                      z={z}
                      akz={zeilenTon(z).akz}
                      schritte={schritte}
                      onOeffnen={() => oeffnen(z)}
                      onToast={toastZeigen}
                    />
                  ))}
              </div>
            ))}

            <div className="li-fuss">
              {gefiltert.length === 0
                ? 'Kein Vorgang passt zu Filter und Suche.'
                : `${gefiltert.length} von ${zeilen.length} Vorgängen` +
                  (gruppierung !== 'keine'
                    ? ` · gruppiert nach ${GRUPPEN.find((x) => x.key === gruppierung)!.label}`
                    : '') +
                  (suche.trim() ? ` · Suche „${suche.trim()}"` : '')}
            </div>
          </div>

          <Rechnungskarte />

          {/*
            Das Archiv: abgenommen und abgeschlossen. Raus aus der Arbeitsliste,
            aber nie gelöscht – bei einer Reklamation muss in zwei Klicks
            feststehen, wann abgenommen wurde und was das Protokoll sagt.
          */}
          <section className={`aw-archiv ${archivOffen ? 'offen' : ''}`}>
            <button type="button" className="aw-archiv-kopf" onClick={() => setArchivOffen((o) => !o)}>
              <span className="aw-archiv-pfeil">{archivOffen ? '▾' : '▸'}</span>
              <IconArchiv />
              <span className="aw-archiv-titel">Auftragsarchiv</span>
              <span className="aw-archiv-anzahl">
                {daten.archivGesamt} {daten.archivGesamt === 1 ? 'Auftrag' : 'Aufträge'}
              </span>
              <span className="aw-archiv-zusatz">abgenommen und abgeschlossen</span>
            </button>

            {archivOffen && (
              <div className="aw-archiv-inhalt">
                <div className="aw-archiv-werkzeuge">
                  <input
                    className="aw-suche"
                    placeholder="Nummer, Kunde, Jahr oder Gewerk …"
                    value={archivSuche}
                    onChange={(e) => setArchivSuche(e.target.value)}
                  />
                  {!archivAlle && !archivSuche && daten.archivGesamt > 5 && (
                    <button type="button" className="aw-knopf" onClick={() => setArchivAlle(true)}>
                      Ganzes Archiv öffnen
                    </button>
                  )}
                </div>

                <div className="aw-archivzeile aw-kopfzeile">
                  <span>Vorgang</span>
                  <span>Abnahme</span>
                  <span>Abnahmeprotokoll</span>
                </div>
                {archivTreffer.map((e) => (
                  <div key={e.angebotId} className="aw-archivzeile">
                    <span className="aw-vorgang">
                      <button type="button" className="aw-nummer" onClick={() => oeffnen(e)}>
                        {e.angebotName}
                      </button>
                      <span className="aw-kunde">
                        {e.kunde}
                        {e.ort ? ` · ${e.ort}` : ''}
                      </span>
                    </span>
                    <span>{e.abnahmeText}</span>
                    <span className={e.protokoll === 'ohne Mängel' ? 'aw-grau' : 'aw-terra'}>
                      {e.protokoll === 'ohne Mängel'
                        ? 'ohne Mängel'
                        : `${e.maengelErledigt ? 'erledigt: ' : 'offen: '}${e.protokoll}`}
                    </span>
                  </div>
                ))}
                {archivTreffer.length === 0 && (
                  <div className="aw-leer">
                    {archivSuche ? 'Nichts gefunden.' : 'Noch kein abgenommener Auftrag.'}
                  </div>
                )}
              </div>
            )}
          </section>
        </>
      )}
      {/* KORA steht unter dem Inhalt der Maske, nicht darüber (v11, 10.2 Regel 6). */}
      <KoraMaskenhinweise maske="auftraege" label="Abwicklung" />
    </div>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Eine Zeile: Vorgang · Fortschritt · Stand · Was fehlt · Liefertermin ·
 * Liegt bei · Ordner (v14, 17.10).
 *
 * Die linke Kante trägt die Rubrikfarbe – dieselbe, die das Kachel-Symbol auf
 * „Mein Tag" hat. Farbe im Text heißt dagegen Zustand: Critical, wenn etwas
 * wirklich hängt, Warning, wenn es zu beachten ist, sonst grau.
 */
function Zeile({
  z,
  akz,
  schritte,
  onOeffnen,
  onToast,
}: {
  z: AbwicklungZeile;
  akz: string;
  schritte: { key: string; lang: string }[];
  onOeffnen: () => void;
  onToast: (text: string) => void;
}) {
  const haengt = z.haenger?.stufe === 'rot';

  return (
    <div className="aw-zeile" style={{ borderLeftColor: akz }}>
      <button type="button" className="li-vorgang" title={`${z.angebotName} öffnen`} onClick={onOeffnen}>
        <span className="li-nummer li-kurz">{z.angebotName}</span>
        <span className="li-kunde li-kurz">
          {z.kunde}
          {z.ort ? ` · ${z.ort}` : ''}
          {z.anzahl > 1 ? ` · Bestellung ${z.nr} von ${z.anzahl}` : ''}
          {z.lieferant ? ` · ${z.lieferant}` : ''}
        </span>
      </button>

      {/*
        Der Fortschritt als neun Striche: Der aktuelle Schritt steht hoch und
        in der Rubrikfarbe, erledigte liegen flach und grau, offene heller.
        Hängt der Vorgang, ist der aktuelle Strich Critical – die einzige
        Stelle, an der die Kette Farbe für einen Zustand trägt.
      */}
      <span className="aw-c-fort aw-fortschritt">
        {schritte.map((s, i) => (
          <span
            key={s.key}
            className={`aw-strich ${i === z.schrittIndex ? 'jetzt' : i < z.schrittIndex ? 'erledigt' : 'offen'}`}
            style={i === z.schrittIndex ? { background: haengt ? 'var(--eh-krit)' : akz } : undefined}
            title={`${s.lang}${i === z.schrittIndex ? ' · aktueller Stand' : i < z.schrittIndex ? ' · erledigt' : ' · offen'}`}
          />
        ))}
      </span>

      <span className="aw-c-stand aw-stand">
        <span className="li-kurz">{z.schrittLang}</span>
        <span className="aw-stand-seit li-kurz">
          seit {z.tageImSchritt} {z.tageImSchritt === 1 ? 'Tag' : 'Tagen'}
        </span>
      </span>

      <span className={`aw-c-fehlt aw-fehlt ${z.haenger ? z.haenger.stufe : 'plan'}`}>
        {z.haenger && <span className="aw-fehlt-punkt" />}
        <span className="li-kurz">{z.haenger ? z.haenger.text : 'im Plan'}</span>
      </span>

      <span className={`aw-c-termin aw-termin ${z.lieferZustand}`}>
        <span className="aw-termin-wert">{z.lieferText}</span>
        {z.lieferUnter && <span className="aw-termin-unter">{z.lieferUnter}</span>}
      </span>

      {/* Avatar und voller Name, ohne Rolle (17.4) – in jeder Liste gleich. */}
      <LiegtBei lb={z.liegtBei} spur />

      <Ordnerzelle art="auftrag" ordnerName={z.ordnerName} onToast={onToast} />
    </div>
  );
}

/**
 * Rechnungen aus sevDesk, die noch keinem Auftrag gehören – und der Knopf,
 * den Abgleich sofort anzustoßen. Kora liest sevDesk nur; die Zuordnung von
 * Hand lernt nebenbei, welcher sevDesk-Kontakt zu welchem Kunden gehört.
 */
function Rechnungskarte() {
  const { toastZeigen } = useKora();
  const queryClient = useQueryClient();
  const [ziel, setZiel] = useState<Record<number, string>>({});

  const abfrage = useQuery({
    queryKey: ['abwicklung-rechnungen'],
    queryFn: () => api.hole<RechnungenAntwort>('/abwicklung/rechnungen'),
  });
  const daten = abfrage.data;

  const neuLaden = () => {
    void queryClient.invalidateQueries({ queryKey: ['abwicklung-rechnungen'] });
    void queryClient.invalidateQueries({ queryKey: ['abwicklung'] });
    void queryClient.invalidateQueries({ queryKey: ['angebote'] });
  };

  const abgleichen = useMutation({
    mutationFn: () =>
      api.sende<{ aktiv: boolean; geholt: number; neu: number; zugeordnet: number; anzahlungen: number; fehler: string | null }>(
        '/abwicklung/rechnungen/abgleichen',
      ),
    onSuccess: (e) => {
      neuLaden();
      toastZeigen(
        !e.aktiv
          ? 'sevDesk ist nicht angebunden.'
          : e.fehler
            ? `Abgleich fehlgeschlagen: ${e.fehler}`
            : `${e.geholt} Rechnungen gelesen · ${e.neu} neu · ${e.zugeordnet} zugeordnet · ${e.anzahlungen} Anzahlungen gebucht`,
      );
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Abgleich fehlgeschlagen.'),
  });

  const zuordnen = useMutation({
    mutationFn: ({ id, angebotId }: { id: number; angebotId: number | null }) =>
      api.sende<{ toast: string }>(`/abwicklung/rechnungen/${id}/zuordnen`, { angebotId }),
    onSuccess: (a) => {
      neuLaden();
      toastZeigen(a.toast);
    },
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Zuordnen fehlgeschlagen.'),
  });

  if (!daten) return null;

  const stand = daten.stand
    ? new Date(daten.stand).toLocaleString('de-AT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : null;

  return (
    <section className="aw-rechnungen">
      <div className="aw-rechnungen-kopf">
        <div>
          <span className="aw-label">Rechnungen aus sevDesk</span>
          <span className="aw-rechnungen-stand">
            {!daten.aktiv
              ? 'sevDesk ist nicht angebunden'
              : stand
                ? `Stand ${stand} · ${daten.offen.length} ohne Auftrag`
                : 'noch nicht abgeglichen'}
          </span>
        </div>
        <button
          type="button"
          className="aw-knopf"
          disabled={abgleichen.isPending || !daten.aktiv}
          onClick={() => abgleichen.mutate()}
        >
          {abgleichen.isPending ? 'Liest sevDesk …' : 'Jetzt abgleichen'}
        </button>
      </div>

      {daten.offen.length > 0 && (
        <div className="aw-rechnungsliste">
          {daten.offen.map((r) => (
            <div key={r.id} className="aw-rechnungszeile">
              <span className="aw-vorgang">
                <a className="aw-nummer" href={r.url} target="_blank" rel="noreferrer" title="In sevDesk öffnen">
                  {r.nummer || 'ohne Nummer'}
                </a>
                <span className="aw-kunde">
                  {r.kundeName || r.kontaktName || 'Kontakt unbekannt'} · {r.istAnzahlung ? 'Anzahlung' : r.artText} ·{' '}
                  {r.datumText} · {r.statusText}
                </span>
              </span>
              <span className="aw-rechnung-betrag">{r.bruttoText}</span>
              <select
                className="aw-auswahl"
                value={ziel[r.id] ?? ''}
                onChange={(e) => setZiel((z) => ({ ...z, [r.id]: e.target.value }))}
              >
                <option value="">Auftrag wählen …</option>
                {daten.ziele.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
                <option value="kein">kein Auftrag</option>
              </select>
              <button
                type="button"
                className="aw-knopf"
                disabled={!ziel[r.id] || zuordnen.isPending}
                onClick={() =>
                  zuordnen.mutate({ id: r.id, angebotId: ziel[r.id] === 'kein' ? null : Number(ziel[r.id]) })
                }
              >
                Zuordnen
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function IconArchiv() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
      <path d="M3 7h18v3H3z" />
      <path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9" />
      <path d="M10 14h4" />
    </svg>
  );
}
